import React, { useEffect, useState, useRef } from "react";
import ApiService from "../../../Services/user/service";
import socket from "../../../Utils/socketClient";

export default function OptionChain() {
  const [expiryList, setExpiryList] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState("");
  const [optionChain, setOptionChain] = useState([]);
  const [livePrice, setLivePrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [symbolSearch, setSymbolSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expiryListInr, setExpiryListInr] = useState([]);
  const userid = localStorage.getItem("user_id");
  const currency = localStorage.getItem("currentCurrency");
  // Order placement states
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    symbol: "",
    price: 0,
    type: "buy",
    optionType: "", // CE or PE
    strike: 0,
    selectedOption: "Market", // Market, Limit, Stop
    lot: 1,
    qty: 1,
    lotsize: 1,
  });
  const [orderLoading, setOrderLoading] = useState(false);
  const [listDataInr, setListDataInr] = useState([]);
  const currentExpiryRef = useRef("");
  const APP_VERSION = "1.0.0"; // Update this with your app version
  const dropdownRef = useRef(null);




  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setSearchResults([]);   // Dropdown close
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // 1️⃣ SOCKET CONNECTION STATUS
  useEffect(() => {
    const handleConnect = () => {
      setSocketConnected(true);

      const userPlan = localStorage.getItem("user_plan") || "Basic";
      socket.emit("join_plan", userPlan);

      if (currentExpiryRef.current) {
        socket.emit("subscribe_expiry", currentExpiryRef.current);
      }
    };

    const handleDisconnect = () => {
      setSocketConnected(false);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, []);

  // 2️⃣ FETCH EXPIRY LIST
  useEffect(() => {
    const fetchExpiry = async () => {
      try {
        const res = await ApiService.getExpiry();
        if (res.data.status) {
          setExpiryList(res.data.data);
        }
      } catch (err) {
        console.error("❌ Expiry fetch error:", err);
      }
    };

    fetchExpiry();
  }, []);

  // 🔥 AUTO SELECT FIRST EXPIRY FOR USD
useEffect(() => {
  if (currency === "0" && expiryList.length > 0) {
    const firstExpiry = expiryList[0];

    // agar pehle se same expiry set hai to dobara mat chalao
    if (currentExpiryRef.current === firstExpiry) return;

    console.log("🔥 Auto selecting first expiry:", firstExpiry);

    setSelectedExpiry(firstExpiry);
    currentExpiryRef.current = firstExpiry;

    // 🔥 API CALL
    fetchStrikeData(firstExpiry);

    // 🔥 SOCKET SUBSCRIBE
    if (socket.connected) {
      socket.emit("subscribe_expiry", firstExpiry);
      console.log("📡 Auto subscribed expiry:", firstExpiry);
    }
  }
}, [expiryList, currency, socketConnected]);


  // 3️⃣ FETCH STRIKE LIST
  const fetchStrikeData = async (expiry) => {
    setLoading(true);

    try {
      const res = await ApiService.createStrike({
        expiry,
        userid: localStorage.getItem("user_id"),
      });

      if (res.data.status) {
        const mapped = res.data.data.map((item) => ({
          ...item,
          strike: Number(item.strike) || 0,
          CE: Number(item.CE) || 0,
          PE: Number(item.PE) || 0,
          symbolCE: item.symbolCE || "",
          symbolPE: item.symbolPE || "",
        }));

        console.log("📊 Initial Strike Data:", mapped);
        setOptionChain(mapped);
        setLivePrice(Number(res.data.liveprice) || 0);
      }
    } catch (err) {
      console.error("❌ Strike API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // 4️⃣ HANDLE EXPIRY SELECT
  const handleExpirySelect = (e) => {
    const expiry = e.target.value;
    setSelectedExpiry(expiry);
    currentExpiryRef.current = expiry;

    if (!expiry) {
      setOptionChain([]);
      return;
    }

    fetchStrikeData(expiry);

    if (socket.connected) {
      socket.emit("subscribe_expiry", expiry);
      console.log("📡 Subscribed to expiry:", expiry);
    }
  };

  // 5️⃣ SOCKET → LIVE UPDATES
  useEffect(() => {
    const dataHandler = (payload) => {
      if (!payload) return;

      const { data } = payload;

      // UPDATE BTC LIVE PRICE
      if (data?.ticker?.toLowerCase() === "btcusd") {
        const newPrice = Number(data.Mid_Price);
        if (!isNaN(newPrice) && newPrice > 0) {
          setLivePrice(newPrice);
          setLastUpdate(new Date().toLocaleTimeString());
        }
      }

      // UPDATE STRIKE PRICES (CE/PE)
      if (data?.ticker && currentExpiryRef.current) {
        const ticker = data.ticker.toLowerCase();

        setOptionChain((prev) => {
          let updated = false;

          const newChain = prev.map((item) => {
            if (item.symbolCE && item.symbolCE.toLowerCase() === ticker) {
              const cePrice = Number(data.Mid_Price);
              if (!isNaN(cePrice) && cePrice > 0) {
                updated = true;
                return { ...item, CE: cePrice };
              }
            }

            if (item.symbolPE && item.symbolPE.toLowerCase() === ticker) {
              const pePrice = Number(data.Mid_Price);
              if (!isNaN(pePrice) && pePrice > 0) {
                updated = true;
                return { ...item, PE: pePrice };
              }
            }

            return item;
          });

          if (updated) {
            setLastUpdate(new Date().toLocaleTimeString());
          }

          return updated ? newChain : prev;
        });
      }
    };

    socket.on("receive_data_forex", dataHandler);
    return () => socket.off("receive_data_forex", dataHandler);
  }, []);

  // 6️⃣ OPEN ORDER MODAL
  const openOrderModal = (symbol, price, type, optionType, strike) => {
    setOrderDetails({
      symbol,
      price,
      type,
      optionType,
      strike,
      selectedOption: "Market",
      lot: 1,
      qty: 1,
      lotsize: 1,
    });
    setShowOrderModal(true);
  };

  // 7️⃣ PLACE ORDER
  const handlePlaceOrder = async () => {
    setOrderLoading(true);

    try {
      const userid = localStorage.getItem("user_id");

      if (!userid) {
        alert("User not logged in!");
        setOrderLoading(false);
        return;
      }

      const requiredFund = orderDetails.price * orderDetails.qty * orderDetails.lot;

      const orderPayload = {
        userid,
        symbol: orderDetails.symbol,
        price: orderDetails.selectedOption === "Market"
          ? orderDetails.price
          : orderDetails.limitStopPrice || orderDetails.price,
        lot: orderDetails.lot,
        qty: orderDetails.qty,
        requiredFund: parseFloat(requiredFund.toFixed(2)),
        type: orderDetails.type.toLowerCase(),
        lotsize: orderDetails.lotsize,
        With_Margin: false,
        selectedOption: orderDetails.selectedOption,
        token: orderDetails.symbol,
        margin_type: "normal",
        app_version: "1.0.19",
      };

      // console.log("📤 Order Payload:", orderPayload);

      const response = await ApiService.placeOrder(orderPayload);

      if (response.data.status) {
        alert(`✅ Order Placed Successfully!\nOrder ID: ${response.data.order._id}`);
        setShowOrderModal(false);

        // Reset order details
        setOrderDetails({
          symbol: "",
          price: 0,
          type: "buy",
          optionType: "",
          strike: 0,
          selectedOption: "Market",
          lot: 1,
          qty: 1,
          lotsize: 1,
        });
      } else {
        alert(`❌ Order Failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error("❌ Order Error:", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setOrderLoading(false);
    }
  };



  // 8️⃣ CALCULATE ATM STRIKE
  const atmStrike =
    livePrice && optionChain.length > 0
      ? optionChain.reduce((prev, curr) =>
        Math.abs(curr.strike - livePrice) < Math.abs(prev.strike - livePrice)
          ? curr
          : prev
      )
      : null;


  const handleSymbolSearch = async (value) => {

    try {
      setSearchLoading(true);

      const res = await ApiService.optionSymbolSearch({
        symbol: value,
      });

      if (res.data.status) {
        setSearchResults(res.data.data);
        handleExpirySelectInr(value);
        // handleshowalldatainr(value);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error("INR Symbol Search Error:", err);
    } finally {
      setSearchLoading(false);
    }
  };


  const handleExpirySelectInr = async (value) => {
    try {
      const res = await ApiService.optionSymbolExpiry({
        symbol: value,
      });

      if (res.data.status) {
        const list = res.data.data;
        setExpiryListInr(list);

        if (list.length > 0) {
          const firstExpiry = list[0].expiry;

          setSelectedExpiry(firstExpiry);
          handleshowalldatainr(value, firstExpiry);
        }
      } else {
        setExpiryListInr([]);
        setSelectedExpiry("");
      }
    } catch (err) {
      console.error("INR Symbol Expiry Error:", err);
    }
  }

const handleshowalldatainr = async (value, expiry) => {
  try {
    const data = { userid, symbol: value, expiry };
    const res = await ApiService.optionRoundToken(data);

    if (res.data.status) {
      setListDataInr(res.data.data);

      // 🔥 ALICEBLUE SUBSCRIBE
      socket.emit("subscribe", res.data.channellist);
      console.log("📡 ALICE SUBSCRIBED:", res.data.channellist);
    }
  } catch (err) {
    console.error(err);
  }
};


useEffect(() => {
  const handleAliceTick = (tick) => {
    console.log("🔥 ALICE TICK:", tick);

    if (!tick?.tk || !tick?.lp) return;

    const token = String(tick.tk);
    const price = Number(tick.lp);
    if (!price || price <= 0) return;

    setListDataInr((prev) =>
      prev.map((item) => {
        if (String(item.call_token) === token) {
          return { ...item, call_price: price };
        }
        if (String(item.put_token) === token) {
          return { ...item, put_price: price };
        }
        return item;
      })
    );

    setLastUpdate(new Date().toLocaleTimeString());
  };

  socket.on("alice_ticks", handleAliceTick);

  return () => {
    socket.off("alice_ticks", handleAliceTick);
  };
}, []);




  useEffect(() => {
    socket.on("connect", () => {
  console.log("🟢 AliceBlue socket connected");
  
});

  }, []);


  
  return (
    <div className="container py-4">
      {/* CONNECTION & UPDATE STATUS */}
      <div className="row mb-3">
        <div className="col-md-6">
          <div className={`alert ${socketConnected ? 'alert-success' : 'alert-warning'} mb-0 py-2`}>
            <small className="mb-0">
              {socketConnected ? '🟢 Socket Connected' : '🟡 Socket Disconnected'}
            </small>
          </div>
        </div>
        <div className="col-md-6">
          {lastUpdate && (
            <div className="alert alert-info mb-0 py-2">
              <small className="mb-0">🕐 Last Update: {lastUpdate}</small>
            </div>
          )}
        </div>
      </div>

      {/* HEADER */}
      <div className="card shadow-lg border-0 mb-4 rounded-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <h3 className="fw-bold text-primary mb-0">BTC Option Chain</h3>

            {currency === "0" && livePrice && (
              <div className="badge bg-success fs-5 px-4 py-2">
                Live BTC: <b>${livePrice.toFixed(2)}</b>
              </div>
            )}
          </div>


          <div className="d-flex gap-3">


            {currency === "1" && (
              <div className="mt-4 position-relative " ref={dropdownRef}>
                <label className="fw-semibold mb-2">Search Symbol</label>
                <input
                  type="text"
                  className="form-control shadow-sm rounded-3"
                  placeholder="Type to search..."
                  value={symbolSearch}
                  onChange={(e) => {
                    setSymbolSearch(e.target.value);
                    handleSymbolSearch(e.target.value); // only search list
                  }}

                />

                {/* SEARCH LOADING */}
                {searchLoading && (
                  <div className="mt-1 small text-muted">Searching...</div>
                )}

                {/* DROPDOWN RESULT LIST */}
                {searchResults.length > 0 && (
                  <ul
                    className="list-group position-absolute w-100 shadow border-0"
                    style={{ zIndex: 10, maxHeight: "200px", overflowY: "auto" }}
                  >
                    {searchResults.map((item, index) => (
                      <li
                        key={index}
                        className="list-group-item list-group-item-action"
                        onClick={() => {
                          const symbol = item.symbol;

                          setSymbolSearch(symbol);
                          setSearchResults([]);

                          //  EXACTLY HERE — call both APIs after user selects item
                          handleExpirySelectInr(symbol);
                          handleshowalldatainr(symbol);
                        }}
                      >
                        {item.symbol}
                      </li>

                    ))}
                  </ul>
                )}
              </div>
            )}






            {currency === "1" && (
              <div className="py-2 mt-3 ">
                <label className="fw-semibold ">Select Expiry Date</label>
                <select
                  style={{ minHeight: "40px" }}
                  className="form-select shadow-sm rounded-3"
                  value={selectedExpiry}
                  onChange={(e) => {
                    const expiry = e.target.value;
                    setSelectedExpiry(expiry);
                    handleExpirySelectInr(symbolSearch);
                    handleshowalldatainr(symbolSearch, expiry);
                  }}


                >
                  <option value="">-- Select Expiry --</option>
                  {expiryListInr.map((exp, i) => (
                    <option key={i} value={exp.expiry}>  {exp.expiryDate.split("T")[0]}</option>
                  ))}
                </select>
              </div>
            )}



          </div>


















          {/* EXPIRY DROPDOWN */}
          {currency === "0" && (
            <div className="mt-4">
              <label className="fw-semibold mb-2">Select Expiry Date</label>
              <select
                className="form-select shadow-sm rounded-3"
                value={selectedExpiry}
                onChange={handleExpirySelect}
                disabled={!socketConnected}
              >
                <option value="">-- Select Expiry --</option>
                {expiryList.map((exp, i) => (
                  <option key={i} value={exp}>{exp}</option>
                ))}
              </select>
            </div>
          )}
        </div>

      </div>

      


    
{!loading && optionChain.length > 0 && (
  <div className="card shadow-lg border-0 rounded-4 mt-4">
    <div className="card-body p-0">

      {/* FIXED HEADER */}
      <table className="table table-hover text-center align-middle mb-0">
        <thead className="bg-primary text-white">
          <tr>
            <th className="py-3 ">CALL (CE)</th>
            <th className="py-3">STRIKE</th>
            <th className="py-3">PUT (PE)</th>
          </tr>
        </thead>
      </table>

      {/* SCROLLABLE BODY */}
      <div style={{ maxHeight: "450px", overflowY: "auto" }}>
        <table className="table table-hover text-center align-middle mb-0">
          <tbody>
            {optionChain.map((item, i) => {
              const isATM = atmStrike?.strike === item.strike;

              return (
                <tr
                  key={i}
                  className={isATM ? "bg-warning bg-opacity-25" : ""}
                >
                  {/* CALL OPTION */}
                  <td className="py-3">
                    <div className="d-flex flex-column align-items-center">
                      <div
                        className="text-primary small mb-1"
                        style={{ fontSize: "10px" }}
                      >
                        {item.symbolCE}
                      </div>

                      <div className="fw-bold fs-5 text-success mb-2">
                        ${item.CE.toFixed(2)}
                      </div>

                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() =>
                            openOrderModal(
                              item.symbolCE,
                              item.CE,
                              "buy",
                              "CE",
                              item.strike
                            )
                          }
                          disabled={!socketConnected}
                        >
                          BUY
                        </button>

                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            openOrderModal(
                              item.symbolCE,
                              item.CE,
                              "sell",
                              "CE",
                              item.strike
                            )
                          }
                          disabled={!socketConnected}
                        >
                          SELL
                        </button>
                      </div>
                    </div>
                  </td>

                  {/* STRIKE */}
                  <td className="py-3 fw-bold fs-4">
                    ${item.strike}
                    {isATM && (
                      <span className="badge bg-warning text-dark ms-2">
                        ATM
                      </span>
                    )}
                  </td>

                  {/* PUT OPTION */}
                  <td className="py-3">
                    <div className="d-flex flex-column align-items-center">
                      <div
                        className="text-danger small mb-1"
                        style={{ fontSize: "10px" }}
                      >
                        {item.symbolPE}
                      </div>

                      <div className="fw-bold fs-5 text-danger mb-2">
                        ${item.PE.toFixed(2)}
                      </div>

                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() =>
                            openOrderModal(
                              item.symbolPE,
                              item.PE,
                              "buy",
                              "PE",
                              item.strike
                            )
                          }
                          disabled={!socketConnected}
                        >
                          BUY
                        </button>

                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() =>
                            openOrderModal(
                              item.symbolPE,
                              item.PE,
                              "sell",
                              "PE",
                              item.strike
                            )
                          }
                          disabled={!socketConnected}
                        >
                          SELL
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  </div>
)}





















{currency === "1" && (
      <div className="card shadow-lg border-0 rounded-4 mt-4">
        <div className="card-body p-0">

          {/* FIXED HEADER TABLE */}
          <table className="table table-hover text-center align-middle mb-0">
            <thead className="bg-primary text-white">
              <tr>
                <th className="py-3">CALL (CE)</th>
                <th className="py-3">STRIKE</th>
                <th className="py-3">PUT (PE)</th>
              </tr>
            </thead>
          </table>

          {/* SCROLLABLE BODY */}
          <div style={{ maxHeight: "450px", overflowY: "auto" }}>
            <table className="table table-hover text-center align-middle mb-0">
              <tbody>
                {currency === "1" && listDataInr.map((item, i) => (
                  <tr key={i}>

                    {/* CALL (CE) */}
                    <td className="py-3">
                      <div className="d-flex flex-column align-items-center">

                        <div className="text-primary small mb-1" style={{ fontSize: "10px" }}>
                          {item.call_tradesymbol}
                        </div>

                        <div className="fw-bold fs-5 text-success mb-2">
                          ₹{Number(item.call_price || 0).toFixed(2)}
                        </div>

                        {/* BUY SELL BUTTONS - RESTORED */}
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() =>
                              openOrderModal(
                                item.call_tradesymbol,
                                item.call_price,
                                "buy",
                                "CE",
                                item.strike_price
                              )
                            }
                          >
                            BUY
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                              openOrderModal(
                                item.call_tradesymbol,
                                item.call_price,
                                "sell",
                                "CE",
                                item.strike_price
                              )
                            }
                          >
                            SELL
                          </button>
                        </div>

                      </div>
                    </td>

                    {/* STRIKE */}
                    <td className="py-3 fw-bold fs-4">
                      ₹{item.strike_price}
                    </td>

                    {/* PUT (PE) */}
                    <td className="py-3">
                      <div className="d-flex flex-column align-items-center">

                        <div className="text-danger small mb-1" style={{ fontSize: "10px" }}>
                          {item.put_tradesymbol}
                        </div>

                        <div className="fw-bold fs-5 text-danger mb-2">
                          ₹{Number(item.put_price || 0).toFixed(2)}
                        </div>

                        {/* BUY SELL BUTTONS - RESTORED */}
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() =>
                              openOrderModal(
                                item.put_tradesymbol,
                                item.put_price,
                                "buy",
                                "PE",
                                item.strike_price
                              )
                            }
                          >
                            BUY
                          </button>

                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                              openOrderModal(
                                item.put_tradesymbol,
                                item.put_price,
                                "sell",
                                "PE",
                                item.strike_price
                              )
                            }
                          >
                            SELL
                          </button>
                        </div>

                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
)}









      {/* ORDER MODAL */}
      {showOrderModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {orderDetails.type.toUpperCase()} {orderDetails.optionType} @ Strike ${orderDetails.strike}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowOrderModal(false)}
                  disabled={orderLoading}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Symbol</label>
                  <input
                    type="text"
                    className="form-control"
                    value={orderDetails.symbol}
                    disabled
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Order Type</label>
                  <select
                    className="form-select"
                    value={orderDetails.selectedOption}
                    onChange={(e) => setOrderDetails({ ...orderDetails, selectedOption: e.target.value })}
                  >
                    <option value="Market">Market</option>
                    <option value="Limit">Limit</option>
                    <option value="Stop">Stop</option>
                  </select>
                </div>

                {(orderDetails.selectedOption === "Limit" || orderDetails.selectedOption === "Stop") && (
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Limit/Stop Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.limitStopPrice || orderDetails.price}
                      onChange={(e) => setOrderDetails({ ...orderDetails, limitStopPrice: e.target.value })}
                      step="0.01"
                    />
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label fw-semibold">Price</label>
                  <input
                    type="number"
                    className="form-control"
                    value={orderDetails.price}
                    disabled
                  />
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Lot</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.lot}
                      onChange={(e) => setOrderDetails({ ...orderDetails, lot: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.qty}
                      onChange={(e) => setOrderDetails({ ...orderDetails, qty: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label fw-semibold">Lot Size</label>
                    <input
                      type="number"
                      className="form-control"
                      value={orderDetails.lotsize}
                      onChange={(e) => setOrderDetails({ ...orderDetails, lotsize: parseInt(e.target.value) || 1 })}
                      min="1"
                    />
                  </div>
                </div>

                <div className="alert alert-info">
                  <strong>Required Fund:</strong> $
                  {(orderDetails.price * orderDetails.qty * orderDetails.lot).toFixed(2)}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowOrderModal(false)}
                  disabled={orderLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-${orderDetails.type === 'buy' ? 'success' : 'danger'}`}
                  onClick={handlePlaceOrder}
                  disabled={orderLoading}
                >
                  {orderLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    `Confirm ${orderDetails.type.toUpperCase()}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    
    </div>
  );
}